import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapPin } from 'lucide-react';
import { ComboBox } from '@/shared/components/ui/ComboBox';
import { RootState, AppDispatch } from '@/store/store';
import { setAccessibleLocations, setSelectedLocation } from '@/store/locationSlice';
import { locationService } from '@/domains/internal/facilities/services/locationService';
import { useLayoutData } from '@/shared/contexts/LayoutDataContext';

interface LocationSelectorProps {
  className?: string;
  showAllOption?: boolean; // Show "All Locations" option for users with view_all permission
  onChange?: (locationId: string | null) => void; // Callback when location changes
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  className = '',
  showAllOption = true,
  onChange,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { accessibleLocations, selectedLocationId, hasViewAllPermission } = useSelector(
    (state: RootState) => state.location
  );

  // Get locations from LayoutDataContext (fetched in parallel with other layout data)
  const { locations, loading } = useLayoutData();

  // Update Redux store when locations are loaded from context
  useEffect(() => {
    if (locations && accessibleLocations.length === 0) {
      dispatch(
        setAccessibleLocations({
          locations: locations.locations,
          hasViewAll: locations.has_view_all_permission,
        })
      );
    }
  }, [locations, dispatch, accessibleLocations.length]);

  const handleLocationChange = (value: string) => {
    const locationId = value === 'all' ? null : value;
    dispatch(setSelectedLocation(locationId));
    onChange?.(locationId);
  };

  // Build options for ComboBox
  const options = [
    // Add "All Locations" option if user has permission and showAllOption is true
    ...(hasViewAllPermission && showAllOption
      ? [{ value: 'all', label: 'All Locations' }]
      : []),
    // Add accessible locations
    ...accessibleLocations.map((loc) => ({
      value: loc.location_id,
      label: locationService.formatLocationLabel(loc),
    })),
  ];

  // Get current value for ComboBox
  const currentValue = selectedLocationId === null ? 'all' : selectedLocationId;

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <MapPin className="w-5 h-5 text-[var(--color-primary)]" />
        <span className="text-sm text-[#535252]">Loading locations...</span>
      </div>
    );
  }

  if (accessibleLocations.length === 0) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <MapPin className="w-5 h-5 text-[var(--color-primary)]" />
        <span className="text-sm text-[#535252]">No locations assigned</span>
      </div>
    );
  }

  // If only one location and no view_all permission, show read-only
  if (accessibleLocations.length === 1 && !hasViewAllPermission) {
    const location = accessibleLocations[0];
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <MapPin className="w-5 h-5 text-[var(--color-primary)]" />
        <span className="text-sm font-medium text-[#535252]">
          {locationService.formatLocationLabel(location)}
        </span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <MapPin className="w-5 h-5 text-[var(--color-primary)] flex-shrink-0" />
      <ComboBox
        options={options}
        value={currentValue}
        onChange={handleLocationChange}
        placeholder="Select location"
        searchable={false}
        clearable={false}
        className="min-w-[200px]"
        height="h-auto"
        padding="px-3 py-2"
        fontSize="text-sm"
      />
    </div>
  );
};

export default LocationSelector;
